import dynamic from 'next/dynamic';
import {DrupalNode, DrupalTaxonomyTerm} from 'next-drupal';

import {
  DrupalProductNode,
  DrupalVideoMedia,
  IndustrySegmentsTaxonomyTerm,
  ProductTypeTaxonomyTerm,
} from '@/types/hygiena-types';

const NodeArticle = dynamic(() => import('@/components/templates/nodes/node--article').then(mod => mod.NodeArticle));
const NodeBasicPage = dynamic(() => import('@/components/templates/nodes/node--page').then(mod => mod.NodeBasicPage));
const NodeEvent = dynamic(() => import('@/components/templates/nodes/node--event').then(mod => mod.NodeEvent));
const NodeLandingPage = dynamic(() => import('@/components/templates/nodes/node--landing_page').then(mod => mod.NodeLandingPage));
const NodeHtmlPack = dynamic(() => import('@/components/templates/nodes/node--html_pack').then(mod => mod.NodeHtmlPack));
const NodeProduct = dynamic(() => import('@/components/templates/nodes/node--product').then(mod => mod.NodeProduct));
const TaxonomyIndustrySegments = dynamic(() => import('@/components/templates/taxonomy/taxonomy--industry_segments').then(mod => mod.TaxonomyIndustrySegments));
const TaxonomyProductClass = dynamic(() => import('@/components/templates/taxonomy/taxonomy--product_class').then(mod => mod.TaxonomyProductClass));
const TaxonomyProductType = dynamic(() => import('@/components/templates/taxonomy/taxonomy--product_type').then(mod => mod.TaxonomyProductType));
const MediaVideo = dynamic(() => import('@/components/templates/media/media--video').then(mod => mod.MediaVideo));
const NodeKeyValuePage = dynamic(() => import('@/components/templates/nodes/node--key_value_page').then(mod => mod.NodeKeyValuePage));

interface EntityRendererProps {
  entity: DrupalNode | DrupalTaxonomyTerm | DrupalVideoMedia;
  additionalContent?: any;
}

export function EntityRenderer({entity, additionalContent}: EntityRendererProps) {
  const key = `page--${entity?.id}`;

  return (
    <>
      {entity?.type === 'media--video' && (
        // @ts-ignore
        <MediaVideo video={entity as DrupalVideoMedia} additionalContent={additionalContent}/>
      )}
      {entity?.type === 'node--page' && (
        <NodeBasicPage key={key} node={entity as DrupalNode}/>
      )}
      {entity?.type === 'node--key_value_page' && (
        <NodeKeyValuePage key={key} node={entity as DrupalNode} additionalContent={additionalContent}/>
      )}
      {entity?.type === 'node--landing_page' && (
        <NodeLandingPage key={key} node={entity as DrupalNode}/>
      )}
      {entity?.type === 'node--html_pack' && (
        <NodeHtmlPack key={key} node={entity as DrupalNode} additionalContent={additionalContent}/>
      )}
      {entity?.type === 'node--article' && (
        <NodeArticle
          key={key}
          node={entity as DrupalNode}
          imageField={entity?.field_article_image}
          categoryTerm={entity?.field_article_type}
          date={entity.created}
          additionalContent={additionalContent}
        />
      )}
      {entity?.type === 'node--press_release' && (
        <NodeArticle
          key={key}
          node={entity as DrupalNode}
          imageField={entity?.field_pr_image}
          date={entity.created}
          additionalContent={additionalContent}
        />
      )}
      {entity?.type === 'node--event' && (
        <NodeEvent
          key={key}
          node={entity as DrupalNode}
          date={entity?.field_event_start_date}
          categoryTerm={entity?.field_event_type}
          additionalContent={additionalContent}
          layout={entity?.field_event_layout}
        />
      )}
      {entity?.type === 'node--product' && (
        // eslint-disable-next-line react/jsx-no-undef
        <NodeProduct
          key={key}
          node={entity as DrupalProductNode}
          additionalContent={additionalContent as {relatedNews: DrupalNode[], extraProducts: DrupalProductNode[]}}
        />
      )}
      {entity?.type === 'taxonomy_term--product_type' && (
        <>
          {(Array.isArray(additionalContent?.['products']) && additionalContent?.['products'].length === 1) ? (
            // @todo let's handle this better for when we're rendering 1 product in a Product Type/Name.
            <NodeProduct
              key={key}
              node={additionalContent['products'][0] as DrupalProductNode}
              additionalContent={{
                relatedNews: additionalContent['relatedNews'],
              }}/>
          ) : (
            <TaxonomyProductType
              key={key}
              additionalContent={additionalContent as {
                subtypes: DrupalTaxonomyTerm[], products: DrupalProductNode[],
                complementaryProducts: DrupalProductNode[], relatedProducts: DrupalProductNode[],
                childTerms: DrupalTaxonomyTerm[], relatedNews: DrupalNode[]
              }}
              taxonomy_term={entity as ProductTypeTaxonomyTerm}
            />
          )}
        </>
      )}
      {entity?.type === 'taxonomy_term--industry_segments' && (
        <>
          {(Array.isArray(additionalContent?.['products']) && additionalContent?.['products'].length === 1) ? (
            // @todo let's handle this better for when we're rendering 1 product in a Product Type/Name.
            <NodeProduct
              key={key}
              node={additionalContent['products'][0] as DrupalProductNode}
              additionalContent={{
                relatedNews: additionalContent['relatedNews'],
              }}/>
          ) : (
            <TaxonomyIndustrySegments
              key={key}
              additionalContent={additionalContent as {
                childTerms: IndustrySegmentsTaxonomyTerm[],
                products: DrupalProductNode[],
                relatedNews: DrupalNode[],
              }}
              taxonomy_term={entity as IndustrySegmentsTaxonomyTerm}/>
          )}
        </>
      )}
      {entity?.type === 'taxonomy_term--product_class' && (
        <>
          {(Array.isArray(additionalContent?.['products']) && additionalContent?.['products'].length === 1) ? (
            // @todo let's handle this better for when we're rendering 1 product in a Product Type/Name.
            <NodeProduct
              key={key}
              node={additionalContent['products'][0] as DrupalProductNode}
              additionalContent={{
                relatedNews: additionalContent['relatedNews'],
              }}/>
          ) : (
            <TaxonomyProductClass
              key={key}
              additionalContent={additionalContent as {
                children?: DrupalTaxonomyTerm[]
                products?: DrupalProductNode[]
                relatedNews?: DrupalNode[]
              }}
              taxonomy_term={entity as DrupalTaxonomyTerm}
            />
          )}
        </>
      )}
    </>
  );
}
